import {
    FraudCheckState,
    FraudCheckQueueListResult,
    FraudCheckUser,
    FraudCheckPage,
    FraudCheckAdjudicationStatus,
    FraudCheckQueueFilterModel,
    FraudCheckQueueDataOverride,
    FraudCheckActionLog
} from "../types";
import * as fraudDetectionService from "@/shared/api/fraudDetectionService";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import { AxiosError } from "axios";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: FraudCheckState = {
    fraudCheckUsers: [],
    pageNumbers: [],
    currentFraudCheckUser: null,
    currentQueueId: 0,
    searchTerm: "",
    fraudCheckLogs: []
};

export const page: FraudCheckPage = {
    hasMore: false,
    isLoading: true,
    items: [],
};

export const useFraudCheckStore = defineStore("fraudCheck", {
        state: (): FraudCheckState => (_.cloneDeep(initialState)),
        getters: {
            getFraudCheckListByPageNumber: state => (pageNumber: number): FraudCheckPage => {
                const pageObject = state.pageNumbers[pageNumber];
                if (typeof pageObject === "undefined") {
                    return { ...page };
                }
                const newPage: FraudCheckPage = {
                    hasMore: pageObject.hasMore,
                    isLoading: false,
                    items: state.fraudCheckUsers.slice(pageObject.index, pageObject.index + pageObject.pageSize),
                };
                return newPage;
            },
            getSearchTerm: state => state.searchTerm,
            getEmailOfSsnConflict: state => state.currentFraudCheckUser
                ? state.currentFraudCheckUser.emailOfSsnConflict
                : null,
        },
        actions: {
            getFraudCheckList (data: { pageNumber: number, searchTerm: string, filters: FraudCheckQueueFilterModel[] }) {
                return new Promise((resolve, reject) => {
                    const pageObject = this.pageNumbers[data.pageNumber || 1];
                    if (typeof pageObject !== "undefined" && this.searchTerm === data.searchTerm) {
                        return Promise.resolve();
                    } else {
                        return fraudDetectionService.getFraudCheckList(data.pageNumber, data.searchTerm, data.filters)
                            .then(resp => {
                                this.setFraudCheckList( resp.data);
                            }).catch(err => {
                                storeHelper.handleError({ error: err, rethrow: true });
                            });
                    }
                });
            },
            fraudDetailLoad (queueId: number): Promise<FraudCheckUser> {
                const e = this.currentFraudCheckUser && this.currentQueueId === queueId;
                if (e) return Promise.resolve(this.currentFraudCheckUser);
                return fraudDetectionService.getFraudCheckDetail(queueId)
                    .then(r => {
                        this.setFraudCheckDetail( r.data);
                        return Promise.resolve(r.data);
                    })
                    .catch((err: AxiosError | any) => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },
            fraudCheckLogsLoad (queueId: number): Promise<FraudCheckActionLog[]> {
                return fraudDetectionService.getFraudCheckLogs(queueId)
                    .then(r => {
                        return Promise.resolve(r.data);
                    })
                    .catch((err: AxiosError | any) => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },
            setFraudCheckDetailAccepted (queueId: number): Promise<boolean> {
                return fraudDetectionService.setFraudCheckDetailAccepted(queueId)
                    .then(r => {
                        this.clearFraudCheckList();
                        return Promise.resolve(r.data);
                    })
                    .catch((err: AxiosError | any) => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },
            setFraudCheckDetailRejected (queueId: number): Promise<boolean> {
                return fraudDetectionService.setFraudCheckDetailRejected(queueId)
                    .then(r => {
                        this.clearFraudCheckList();
                        return Promise.resolve(r.data);
                    })
                    .catch((err: AxiosError | any) => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },
            setFraudCheckAdjudicationStatus (data: {
                queueId: number, adjudicationStatus
                    : FraudCheckAdjudicationStatus
            }): Promise<boolean> {
                return fraudDetectionService.setFraudCheckAdjudicationStatus(data.queueId, data.adjudicationStatus)
                    .then(r => {
                        this.clearFraudCheckList();
                        return Promise.resolve(r.data);
                    })
                    .catch((err: AxiosError | any) => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },

            overrideUserData (
                data: { queueId: number, dataOverride: FraudCheckQueueDataOverride }
            ): Promise<any> {

                return fraudDetectionService
                    .overrideUserData(data.queueId, data.dataOverride)
                    .catch(err => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        return Promise.reject();
                    });
            },
            setFraudCheckList (fraudCheckList: FraudCheckQueueListResult) {
                for (const user of fraudCheckList.fraudCheckUsers) {
                    user.dateCreated = moment(user.dateCreated).format("MM/DD/YYYY");
                }
                const pageNumbers = [ ...this.pageNumbers ];
                pageNumbers[fraudCheckList.pageNumber] = {
                    index: this.fraudCheckUsers.length,
                    hasMore: fraudCheckList.hasMore,
                    pageSize: fraudCheckList.pageSize
                };
                this.fraudCheckUsers =
                    this.fraudCheckUsers.concat(fraudCheckList.fraudCheckUsers);
                this.pageNumbers = pageNumbers;
                this.searchTerm = fraudCheckList.searchTerm ? fraudCheckList.searchTerm : "";
            },
            setFraudCheckDetail (fraudCheckDetail: FraudCheckUser) {
                fraudCheckDetail.dateCreated = moment(fraudCheckDetail.dateCreated).format("MM/DD/YYYY");
                fraudCheckDetail.dateOfBirth = moment(fraudCheckDetail.dateOfBirth).format("MM/DD/YYYY");
                this.currentFraudCheckUser = fraudCheckDetail;
                this.currentQueueId = fraudCheckDetail.queueId;
            },
            clearFraudCheckList () {
                this.pageNumbers = [];
                this.searchTerm = "";
                this.currentQueueId = 0;
                this.currentFraudCheckUser = null;
                this.fraudCheckUsers = [];
            },
            setSearchTerm (searchTerm: string) {
                this.searchTerm = searchTerm;
            },
        }
    }
);
